import { collection, deleteDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import type {
  AreaReportSnapshotAxisCount,
  EvaluationPolarityCounts,
  RouteReportSnapshot,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

export interface SaveRouteReportSnapshotInput {
  districtId: string;
  name: string;
  lineCoordinates: { latitude: number; longitude: number }[];
  lengthM: number;
  postIds: string[];
  pointCount: number;
  axisCounts: AreaReportSnapshotAxisCount[];
  summary: string | null;
  /** 集計に使った調査項目の key (docs/27 §9)。 */
  axisKey?: string | null;
  /** 評価の内訳 (評価軸を持つテンプレートのみ)。 */
  evaluation?: EvaluationPolarityCounts | null;
  /** 集計した調査 (docs/98 R-97、未指定 = 既定の調査)。 */
  surveyKey?: string | null;
}

/**
 * 路線調査 (地図で線を描く一括解析) を名前付きスナップショットとして保存する
 * (`saveAreaReportSnapshot` と完全に同じ「client SDK が直接書込み、firestore.rules の
 * isDistrictAdmin に委任」方式。docs/25 §10-6)。
 */
export async function saveRouteReportSnapshot(
  input: SaveRouteReportSnapshotInput,
  uid: string,
): Promise<string> {
  const ref = doc(collection(db, 'districts', input.districtId, 'routeReports'));
  const data: Omit<RouteReportSnapshot, 'createdAt'> & {
    createdAt: ReturnType<typeof serverTimestamp>;
  } = {
    districtId: input.districtId,
    name: input.name,
    lineCoordinates: input.lineCoordinates,
    lengthM: input.lengthM,
    postIds: input.postIds,
    pointCount: input.pointCount,
    axisCounts: input.axisCounts,
    axisKey: input.axisKey ?? null,
    evaluation: input.evaluation ?? null,
    surveyKey: input.surveyKey && input.surveyKey !== 'default' ? input.surveyKey : null,
    summary: input.summary,
    createdAt: serverTimestamp(),
    createdBy: uid,
  };
  await setDoc(ref, data);
  return ref.id;
}

export async function deleteRouteReportSnapshot(
  districtId: string,
  reportId: string,
): Promise<void> {
  await deleteDoc(doc(db, 'districts', districtId, 'routeReports', reportId));
}
